import { useEffect, useRef, useState } from "react";
import { CalendarClock, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

/**
 * Mini-modal to schedule (or re-schedule) a task from "Cicli da aprire".
 * Fields: date (required), time (optional), duration min (optional).
 *
 * Props:
 *   - open       boolean
 *   - task       task object (must have .id, .title; may pre-fill scheduled_*)
 *   - onClose    fn
 *   - onSaved    fn(updatedTask) — parent should refresh the list
 *   - title      override title (default: "Metti in agenda")
 */
export default function MettiInAgendaModal({ open, task, onClose, onSaved,
                                              title = "Metti in agenda" }) {
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [duration, setDuration] = useState("");
  const [busy, setBusy] = useState(false);
  const firstInputRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    // pre-fill from task if present, else today
    const today = new Date();
    const iso = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
    setDate(task?.scheduled_date || iso);
    setTime((task?.scheduled_time || "").slice(0, 5));
    setDuration(task?.scheduled_duration_min ? String(task.scheduled_duration_min) : "");
    setTimeout(() => firstInputRef.current?.focus(), 30);
  }, [open, task]);

  if (!open || !task) return null;

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!date) { toast.error("La data è obbligatoria"); return; }
    setBusy(true);
    try {
      const payload = {
        scheduled_date: date,
        scheduled_time: time || null,
        scheduled_duration_min: duration ? Number(duration) : null,
      };
      const updated = await api.updateTask(task.id, payload);
      const [Y, M, D] = date.split("-");
      const humanDate = `${D}/${M}/${Y}`;
      const humanTime = time ? ` alle ${time}` : "";
      toast.success(`Ciclo messo in agenda per il ${humanDate}${humanTime}`);
      onSaved?.(updated);
      onClose?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore programmazione");
    } finally { setBusy(false); }
  };

  return (
    <div
      role="dialog" aria-modal="true"
      className="fixed inset-0 z-50 grid place-items-center p-4 bg-black/40 backdrop-blur-sm"
      data-testid="metti-in-agenda-modal"
      onClick={(e) => { if (e.target === e.currentTarget && !busy) onClose?.(); }}
    >
      <div className="pxos-card w-full max-w-md p-6 space-y-4 relative">
        <button
          type="button" onClick={() => !busy && onClose?.()}
          className="absolute top-3 right-3 opacity-60 hover:opacity-100 p-1"
          aria-label="Chiudi"
          data-testid="metti-in-agenda-close"
        >
          <X size={18} />
        </button>

        <div className="flex items-start gap-3">
          <div className="rounded-full bg-blue-100 p-2 text-blue-700">
            <CalendarClock size={20} />
          </div>
          <div className="flex-1">
            <h2 className="font-display text-lg font-semibold" data-testid="metti-in-agenda-title">
              {title}
            </h2>
            <p className="text-sm opacity-70 mt-0.5 line-clamp-2" data-testid="metti-in-agenda-task-title">
              {task.title}
            </p>
          </div>
        </div>

        <form onSubmit={submit} className="space-y-3">
          <div>
            <label className="text-xs font-medium opacity-80">
              Data programmata <span className="text-red-600">*</span>
            </label>
            <input
              ref={firstInputRef}
              type="date" required
              value={date}
              onChange={(e) => setDate(e.target.value)}
              data-testid="metti-in-agenda-date"
              className="mt-1 w-full rounded-md border px-3 py-2 text-sm bg-white"
              style={{ borderColor: "var(--pxos-border)" }}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium opacity-80">Ora (opzionale)</label>
              <input
                type="time"
                value={time}
                onChange={(e) => setTime(e.target.value)}
                data-testid="metti-in-agenda-time"
                className="mt-1 w-full rounded-md border px-3 py-2 text-sm bg-white"
                style={{ borderColor: "var(--pxos-border)" }}
              />
            </div>
            <div>
              <label className="text-xs font-medium opacity-80">Durata (min)</label>
              <input
                type="number" min="5" max="480" step="5"
                placeholder="30"
                value={duration}
                onChange={(e) => setDuration(e.target.value)}
                data-testid="metti-in-agenda-duration"
                className="mt-1 w-full rounded-md border px-3 py-2 text-sm bg-white"
                style={{ borderColor: "var(--pxos-border)" }}
              />
            </div>
          </div>

          <div className="flex items-center justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={() => !busy && onClose?.()}
              className="pxos-btn-secondary"
              data-testid="metti-in-agenda-cancel"
              disabled={busy}
            >
              Annulla
            </button>
            <button
              type="submit"
              disabled={busy || !date}
              className="pxos-btn-primary inline-flex items-center gap-1.5"
              data-testid="metti-in-agenda-submit"
            >
              {busy ? <Loader2 size={14} className="animate-spin" /> : <CalendarClock size={14} />}
              Metti in agenda
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
